import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import DeletePopUp from "../DeletePopUp";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useLoader } from "../../context/LoaderContext";
import { usePermission } from "../../hooks/usePermission";
import { IRole, IRoleModel } from "../../exports/interfaces";
import { fetchRoles } from "../../api/AdminApi";
import ConfirmPopUp from "../ConfirmPopUp";
import { fetchProfile } from "../../api/UserApi";
import { useUser } from "../../context/UserContext";

const RolePermissions = ({
  roles,
  setRoles,
}: {
  roles: IRole[];
  setRoles: Dispatch<SetStateAction<IRole[]>>;
}) => {
  const { setLoading } = useLoader();
  const { setUser } = useUser();
  const { canCreate, canEdit } = usePermission();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [pendingPermission, setPendingPermission] = useState<{
    roleModel: IRoleModel;
    field: "view" | "create" | "edit" | "delete";
    checked: boolean;
  } | null>(null);

  const [showPermissionConfirm, setShowPermissionConfirm] = useState(false);
  const selectedRole = roles.find((role) => role.id === selectedRoleId) || null;

  useEffect(() => {
    if (!selectedRoleId && roles.length > 0) {
      setSelectedRoleId(roles[0].id ?? null);
    }
  }, [roles, selectedRoleId]);

  const handleClose = () => {
    setShowDelete(false);
  };

  const handlePermissionClick = (
    roleModel: IRoleModel,
    field: "view" | "create" | "edit" | "delete",
    checked: boolean,
  ) => {
    setPendingPermission({ roleModel, field, checked });
    setShowPermissionConfirm(true);
  };

  const handlePermissionConfirm = async () => {
    if (!pendingPermission || !selectedRoleId) return;

    const { roleModel, field, checked } = pendingPermission;
    if (!roleModel.id) return;

    const previousRoles = roles;

    try {
      setLoading(true);

      setRoles((prevRoles) =>
        prevRoles.map((role) =>
          role.id === selectedRoleId
            ? {
                ...role,
                models: role.models?.map((model) =>
                  model.id === roleModel.id
                    ? { ...model, [field]: checked }
                    : model,
                ),
              }
            : role,
        ),
      );

      await axiosClient.put(`/options/role-models/${roleModel.id}`, {
        [field]: checked,
      });

      await fetchProfile().then((userData) => {
        setUser(userData);
      });

      dynamicSuccess("Permission updated successfully!");
      setShowPermissionConfirm(false);
      setPendingPermission(null);
    } catch (error: any) {
      setRoles(previousRoles);
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handlePermissionCancel = () => {
    setPendingPermission(null);
    setShowPermissionConfirm(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/options/roles/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        const roleData = await fetchRoles();
        setRoles(roleData);
        setSelectedRoleId(roleData[0]?.id ?? null);
        setShowDelete(false);
        setDeleteId(null);
        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("Role deleted successfully!");
        }, 1000);
      }
    } catch (error: any) {
      setTimeout(() => {
        dynamicFailed(`${error.message}`);
        setLoading(false);
      }, 1000);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      label: newLabel,
      value: newValue,
    };

    try {
      setLoading(true);
      const response = await axiosClient.post<IRole>("/options/roles", payload);

      if (response.status === 200 || response.status === 201) {
        const roleData = await fetchRoles();
        setRoles(roleData);
        const updatedRole =
          roleData.find((role) => role.id === response.data.id) || null;
        setSelectedRoleId(updatedRole?.id ?? null);
        setNewLabel("");
        setNewValue("");
        setShowForm(false);

        dynamicSuccess("Role added successfully!");
      } else {
        dynamicFailed("Failed to add role");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <div className="row g-3">
        {/* Left Sidebar */}
        <div className="col-lg-3 col-md-5">
          <div className="dashboard-card h-100 p-0 overflow-hidden">
            <div className="p-2 border-bottom d-flex justify-content-between align-items-center mb-2">
              <h5 className="primary-color">Roles</h5>
              {canCreate("Admin Control") && (
                <button
                  className="btn btn-badge-primary"
                  onClick={() => setShowForm((prev) => !prev)}
                >
                  <i className="fa-solid fa-plus"></i>
                </button>
              )}
            </div>

            <div
              className={`option-form-wrapper dashboard-card ${showForm ? "open" : "p-0"}`}
            >
              <div className="option-form">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Value (Slug)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="User"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">Label (Display)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="User"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-badge-border px-3"
                    onClick={() => {
                      setShowForm(false);
                      setNewLabel("");
                      setNewValue("");
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSubmit}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div className="list-group list-group-flush custom-list-group">
              {roles.map((role) => (
                <button
                  key={role.id ?? role.value}
                  className={`list-group-item list-group-item-action border-0 d-flex justify-content-between align-items-center text-start ${
                    selectedRoleId === role.id ? "active-category" : ""
                  }`}
                  onClick={() => setSelectedRoleId(role.id ?? null)}
                >
                  <span>{role.label}</span>
                  {selectedRoleId === role.id && (
                    <i
                      className="fa-solid fa-trash text-danger cursor-pointer"
                      data-toggle="tooltip"
                      data-placement="top"
                      title="Delete"
                      onClick={() => {
                        //   setDeleteId(item.id);
                        setDeleteId(role.id ?? null);
                        setShowDelete(true);
                      }}
                    ></i>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-lg-9 col-md-7">
          <div className="dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
              <div>
                <h5 className="primary-color">{selectedRole?.label}</h5>
                <small>Manage available options for this dropdown field</small>
              </div>
            </div>

            <div className="mt-4 table-responsive permission-table table-checkbox">
              <table className="table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>View</th>
                    <th>Create</th>
                    <th>Edit</th>
                    <th>Delete</th>
                  </tr>
                </thead>

                <tbody>
                  {selectedRole?.models?.map((roleModel) => {
                    const isReadOnlyModule =
                      selectedRole?.label?.replace(/\s+/g, "").toLowerCase() ===
                        "superadmin" &&
                      ["Users", "Settings", "Admin Control"].includes(
                        roleModel.model,
                      );

                    return (
                      <tr key={roleModel.id}>
                        <td>{roleModel.model}</td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.view}
                            className="permission-checkbox"
                            disabled={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            readOnly={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            onChange={(e) =>
                              handlePermissionClick(
                                roleModel,
                                "view",
                                e.target.checked,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.create}
                            className="permission-checkbox"
                            disabled={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            readOnly={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            onChange={(event) =>
                              handlePermissionClick(
                                roleModel,
                                "create",
                                event.target.checked,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.edit}
                            className="permission-checkbox"
                            disabled={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            readOnly={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            onChange={(event) =>
                              handlePermissionClick(
                                roleModel,
                                "edit",
                                event.target.checked,
                              )
                            }
                          />
                        </td>

                        <td>
                          <input
                            type="checkbox"
                            checked={roleModel.delete}
                            className="permission-checkbox"
                            disabled={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            readOnly={
                              !canEdit("Admin Control") || isReadOnlyModule
                            }
                            onChange={(event) =>
                              handlePermissionClick(
                                roleModel,
                                "delete",
                                event.target.checked,
                              )
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-3 table-checkbox">
              <div className="d-flex gap-3">
                <div className="d-flex align-items-center gap-2">
                  <input type="checkbox" checked readOnly />
                  Allowed
                </div>

                <div className="d-flex align-items-center gap-2">
                  <input type="checkbox" checked={false} readOnly />
                  Denied
                </div>
              </div>

              <small>
                Changes apply the next time the user refreshes their session
              </small>
            </div>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}

      {showPermissionConfirm && (
        <ConfirmPopUp
          onClose={handlePermissionCancel}
          onConfirm={handlePermissionConfirm}
        />
      )}
    </>
  );
};

export default RolePermissions;
